import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api";
import BirthdayConfettiOverlay from "./BirthdayConfettiOverlay";
const formatHoursMinutes = (hours = 0) => {
  const h = Math.floor(Number(hours));
  const m = Math.round((Number(hours) - h) * 60);

  if (m === 60) {
    return `${h + 1}.00`;
  }

  return `${h}.${String(m).padStart(2, "0")}`;
};
/* ---------------- Layout constants ---------------- */
const PROJECT_COL_WIDTH = 260;
const TOTAL_COL_WIDTH = 90;
const COST_COL_WIDTH = 130;
const EMP_COL_MIN = 110;

/* ---------------- Responsive: window width hook ---------------- */
const useWindowWidth = () => {
  const getW = () => (typeof window !== "undefined" ? window.innerWidth : 1280);
  const [w, setW] = useState(getW);
  useEffect(() => {
    const onResize = () => setW(getW());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return w;
};

/* ---------------- Helper: Notification API ---------------- */
const notify = (title, body) => {
  try {
    if (!("Notification" in window)) return;
    if (Notification.permission === "granted") {
      new Notification(title, { body });
    } else if (Notification.permission !== "denied") {
      Notification.requestPermission().then((perm) => {
        if (perm === "granted") new Notification(title, { body });
      });
    }
  } catch {}
};

const isTodayBirthday = (dob) => {
  if (!dob) return false;

  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return false;

  const today = new Date();

  return (
    d.getUTCDate() === today.getDate() && d.getUTCMonth() === today.getMonth()
  );
};

const getMonthLabel = (isoDate) =>
  new Date(isoDate).toLocaleDateString("en-GB", {
    month: "short",
    year: "numeric",
  });

const sameMonth = (isoDate, label) => getMonthLabel(isoDate) === label;

const MONTH_OPTIONS = [
  { value: "01", label: "Jan" },
  { value: "02", label: "Feb" },
  { value: "03", label: "Mar" },
  { value: "04", label: "Apr" },
  { value: "05", label: "May" },
  { value: "06", label: "Jun" },
  { value: "07", label: "Jul" },
  { value: "08", label: "Aug" },
  { value: "09", label: "Sep" },
  { value: "10", label: "Oct" },
  { value: "11", label: "Nov" },
  { value: "12", label: "Dec" },
];

const getPeriodLabel = (year, month) => {
  if (!year) return "All Time";
  if (!month) return String(year);

  const monthName =
    MONTH_OPTIONS.find((item) => item.value === month)?.label || month;

  return `${monthName} ${year}`;
};

/* ---------------- Tiny SVG Donut Pie Chart ---------------- */
const COLORS = [
  "#6366F1",
  "#F59E0B",
  "#10B981",
  "#EF4444",
  "#8B5CF6",
  "#06B6D4",
  "#EAB308",
  "#14B8A6",
  "#F97316",
  "#22C55E",
  "#3B82F6",
  "#A855F7",
];

function polar(cx, cy, r, angleRad) {
  return [cx + r * Math.cos(angleRad), cy + r * Math.sin(angleRad)];
}

function ringSlicePath(cx, cy, rOuter, rInner, start, end) {
  const largeArc = end - start > Math.PI ? 1 : 0;
  const [sx, sy] = polar(cx, cy, rOuter, start);
  const [ex, ey] = polar(cx, cy, rOuter, end);
  const [sx2, sy2] = polar(cx, cy, rInner, end);
  const [ex2, ey2] = polar(cx, cy, rInner, start);
  return [
    `M ${sx} ${sy}`,
    `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${ex} ${ey}`,
    `L ${sx2} ${sy2}`,
    `A ${rInner} ${rInner} 0 ${largeArc} 0 ${ex2} ${ey2}`,
    "Z",
  ].join(" ");
}

/** Donut component (clickable slices) */
const DonutPie = ({
  data,
  size = 300,
  thickness = 58,
  onHover,
  onSliceClick,
}) => {
  const total = data.reduce((s, d) => s + d.value, 0);
  const cx = size / 2;
  const cy = size / 2;
  const rOuter = size / 2 - 10;
  const rInner = rOuter - thickness;

  if (!total) {
    return (
      <div style={{ padding: 12, color: "#6b7280", textAlign: "center" }}>
        No data
      </div>
    );
  }

  const triggerClick = (d, pct, e) => {
    if (typeof onSliceClick === "function") {
      onSliceClick(
        { key: d.key, label: d.label, value: d.value, color: d.color, pct },
        e,
      );
    }
  };

  const keyHandler = (d, pct) => (ev) => {
    if (ev.key === "Enter" || ev.key === " ") {
      ev.preventDefault();
      triggerClick(d, pct, ev);
    }
  };

  if (data.length === 1) {
    const d = data[0];
    const rMid = (rOuter + rInner) / 2;
    const strokeWidth = rOuter - rInner;
    const percent = 100;

    const enter = (e) =>
      onHover?.(
        { label: d.label, value: d.value, color: d.color, pct: percent },
        e,
      );

    const leave = () => onHover?.(null);
    const isClickable = !!onSliceClick;

    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle
          cx={cx}
          cy={cy}
          r={rMid}
          fill="none"
          stroke={d.color}
          strokeWidth={strokeWidth}
          onMouseEnter={enter}
          onMouseMove={enter}
          onMouseLeave={leave}
          onClick={isClickable ? (e) => triggerClick(d, percent, e) : undefined}
          role={isClickable ? "button" : undefined}
          tabIndex={isClickable ? 0 : undefined}
          onKeyDown={isClickable ? keyHandler(d, percent) : undefined}
          style={isClickable ? { cursor: "pointer" } : undefined}
        />
        <text
          x={cx}
          y={cy - rMid + 18}
          textAnchor="middle"
          stroke="#111827"
          strokeWidth="2"
          paintOrder="stroke"
          style={{ fontSize: 12, fontWeight: 800, fill: "#fff" }}
          onMouseEnter={enter}
          onMouseMove={enter}
          onMouseLeave={leave}
        >
          {percent}%
        </text>

        <circle cx={cx} cy={cy} r={rInner - 6} fill="#fff" />
        <text
          x={cx}
          y={cy - 4}
          textAnchor="middle"
          style={{ fontWeight: 800, fontSize: 20, fill: "#111827" }}
        >
          {total.toFixed(0)}
        </text>
        <text
          x={cx}
          y={cy + 16}
          textAnchor="middle"
          style={{ fontSize: 12, fill: "#6b7280" }}
        >
          Hours
        </text>
      </svg>
    );
  }

  let acc = -Math.PI / 2;
  const enter = (d, e, pct) =>
    onHover?.({ label: d.label, value: d.value, color: d.color, pct }, e);
  const move = (d, e, pct) => enter(d, e, pct);
  const leave = () => onHover?.(null);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {data.map((d, i) => {
        const angle = (d.value / total) * Math.PI * 2;
        const start = acc;
        const end = acc + angle;
        acc = end;
        if (angle <= 0) return null;

        const mid = (start + end) / 2;
        const tiny = angle < 0.1;
        const rText = tiny ? (rOuter + rInner) / 2 + 12 : (rOuter + rInner) / 2;
        const [tx, ty] = polar(cx, cy, rText, mid);
        const rawPct = (d.value / total) * 100;
        const percent = d.value > 0 ? Math.max(Math.round(rawPct), 1) : 0;
        const isClickable = !!onSliceClick && d.key !== "__others__";

        return (
          <g key={d.key + i}>
            <path
              d={ringSlicePath(cx, cy, rOuter, rInner, start, end)}
              fill={d.color}
              stroke="#fff"
              strokeWidth="1"
              onMouseEnter={(e) => enter(d, e, percent)}
              onMouseMove={(e) => move(d, e, percent)}
              onMouseLeave={leave}
              onClick={
                isClickable ? (e) => triggerClick(d, percent, e) : undefined
              }
              role={isClickable ? "button" : undefined}
              tabIndex={isClickable ? 0 : undefined}
              onKeyDown={isClickable ? keyHandler(d, percent) : undefined}
              style={isClickable ? { cursor: "pointer" } : undefined}
            />
            <text
              x={tx}
              y={ty + 4}
              textAnchor="middle"
              stroke="#111827"
              strokeWidth="2"
              paintOrder="stroke"
              style={{
                fill: "#fff",
                fontSize: tiny ? 10 : 12,
                fontWeight: 800,
              }}
              onMouseEnter={(e) => enter(d, e, percent)}
              onMouseMove={(e) => move(d, e, percent)}
              onMouseLeave={leave}
            >
              {percent}%
            </text>
          </g>
        );
      })}
      <circle cx={cx} cy={cy} r={rInner - 6} fill="#fff" />
      <text
        x={cx}
        y={cy - 4}
        textAnchor="middle"
        style={{ fontWeight: 800, fontSize: 20, fill: "#111827" }}
      >
        {total.toFixed(0)}
      </text>
      <text
        x={cx}
        y={cy + 16}
        textAnchor="middle"
        style={{ fontSize: 12, fill: "#6b7280" }}
      >
        Hours
      </text>
    </svg>
  );
};
/* -------------------------------------------------------- */

// normalize category
const normalizeCategory = (log) => {
  let cat = (log.projectCategory || "").trim();
  if (!cat) {
    const nm = (log.projectName || "").toLowerCase();
    if (nm.startsWith("us_")) cat = "USA";
    else if (nm.startsWith("can_")) cat = "CANADA";
    else if (nm.includes("arch.ind")) cat = "Arch.ind";
    else if (nm.includes("gruda")) cat = "Gruda";
    else if (nm.includes("dc")) cat = "DC";
    else if (nm.includes("interior")) cat = "Interior";
    else if (nm.includes("finance")) cat = "Finance";
    else if (nm.includes("sales")) cat = "Sales";
    else if (nm.includes("marketing")) cat = "Marketing";
    else cat = "Uncategorized";
  } else {
    const lc = cat.toLowerCase();
    if (lc === "arch.ind") cat = "Arch.ind";
    else if (lc === "gruda") cat = "Gruda";
    else if (lc === "usa") cat = "USA";
    else if (lc === "canada") cat = "CANADA";
    else if (lc === "dc") cat = "DC";
    else if (lc === "projectdata") cat = "ProjectData";
    else if (lc === "interior") cat = "Interior";
    else if (lc === "finance") cat = "Finance";
    else if (lc === "sales") cat = "Sales";
    else if (lc === "marketing") cat = "Marketing";
    else cat = cat.charAt(0).toUpperCase() + cat.slice(1);
  }
  return cat;
};

// Currency format
const formatINR = (n) =>
  (n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });

/* ============ MOBILE LIST: Project-wise Summary ============ */
const MobileProjectSummary = ({ summary }) => {
  return (
    <div style={{ display: "grid", gap: 12 }}>
      {Object.entries(summary).map(([category, projects]) => {
        const totals = Object.values(projects).reduce(
          (acc, d) => {
            acc.h += d.actual || 0;
            acc.c += d.projectCost || 0;
            return acc;
          },
          { h: 0, c: 0 },
        );

        const entries = Object.entries(projects).sort(
          (a, b) => (b[1].projectCost || 0) - (a[1].projectCost || 0),
        );

        return (
          <div key={category} style={mstyles.catCard}>
            <div style={mstyles.catTitle}>{category.toUpperCase()}</div>
            <div style={mstyles.catTotals}>
              <span>
                <b>Total Hours:</b> {totals.h.toFixed(2)}
              </span>
              <span>
                <b>Cost:</b> ₹{formatINR(totals.c)}
              </span>
            </div>

            <div style={{ display: "grid", gap: 10 }}>
              {entries.map(([projName, d], idx) => (
                <div key={projName + idx} style={mstyles.projCard}>
                  <Link
                    to={`/project-cost-report?project=${encodeURIComponent(
                      projName,
                    )}`}
                    state={{ openProjectName: projName }}
                    title="Open in Projects Communication"
                    style={{
                      ...mstyles.projTitle,
                      textDecoration: "none",
                      cursor: "pointer",
                      color: "#1f3a8a",
                      display: "inline-block",
                    }}
                  >
                    {projName}
                  </Link>

                  <div style={mstyles.metaRow}>
                    <div style={mstyles.metaItem}>
                      🕒 {d.actual.toFixed(2)} hrs
                    </div>
                    <div style={mstyles.metaItem}>
                      💰 ₹{formatINR(d.projectCost)}
                    </div>
                  </div>

                  <div style={mstyles.empHeader}>Employee Hours</div>
                  <div style={mstyles.pillsWrap}>
                    {Object.entries(d.employeeHours)
                      .filter(([, h]) => (h || 0) > 0)
                      .sort((a, b) => b[1] - a[1])
                      .map(([emp, h]) => (
                        <span key={emp} style={mstyles.pill}>
                          {emp}: <b>{formatHoursMinutes(h)}</b>
                        </span>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();

  const [showCost, setShowCost] = useState(false);
  const [pinPopup, setPinPopup] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const SECURE_PIN = "8595";

  const [birthdayUsers, setBirthdayUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [empRates, setEmpRates] = useState({});
  const [employeeNames, setEmployeeNames] = useState([]);

  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [useRange, setUseRange] = useState(false);

  const [pendingLeavesCount, setPendingLeavesCount] = useState(0);
  const [lastPendingIds, setLastPendingIds] = useState([]);
  const pollTimerRef = useRef(null);

  const [costSort, setCostSort] = useState("none");
  const [costMin, setCostMin] = useState("");
  const [costMax, setCostMax] = useState("");

  const width = useWindowWidth();
  const isMobile = width < 640;
  const isTablet = width >= 640 && width < 1024;
  const PIE_SIZE = isMobile ? 210 : isTablet ? 260 : 300;
  const PIE_THICKNESS = isMobile ? 40 : isTablet ? 50 : 58;
  const [adminOwnBirthday, setAdminOwnBirthday] = useState("");
  const [showAdminOwnBirthday, setShowAdminOwnBirthday] = useState(false);
  const [tooltip, setTooltip] = useState({
    show: false,
    x: 0,
    y: 0,
    label: "",
    value: 0,
    pct: 0,
    color: "#000",
  });

  const showTooltip = (info, e) => {
    if (!info) return setTooltip((t) => ({ ...t, show: false }));
    setTooltip({
      show: true,
      x: (e?.clientX ?? 0) + 14,
      y: (e?.clientY ?? 0) + 14,
      label: info.label,
      value: info.value,
      pct: info.pct ?? 0,
      color: info.color || "#000",
    });
  };

  // Fetch logs + employees
  useEffect(() => {
    (async () => {
      try {
        const [lRes, uRes] = await Promise.all([
          api.get("/logs/all", {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }),
          api.get("/users", {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }),
        ]);

        setLogs(lRes.data || []);

        const users = uRes.data || [];

        const rateMap = {};
        users.forEach((u) => {
          rateMap[u.name] = u.hourlyRate || 0;
        });
        setEmpRates(rateMap);

        const todaysBirthdays = users.filter((u) =>
          isTodayBirthday(u.dateOfBirth),
        );
        setBirthdayUsers(todaysBirthdays);

        const todayKey = new Date().toDateString();
        const storedKey = localStorage.getItem("birthdayNotifiedAdmin");

        if (todaysBirthdays.length > 0 && storedKey !== todayKey) {
          todaysBirthdays.forEach((u) => {
            notify("🎂 Birthday Reminder", `${u.name} ka aaj birthday hai`);
          });
          localStorage.setItem("birthdayNotifiedAdmin", todayKey);
        }

        if (todaysBirthdays.length === 0) {
          localStorage.removeItem("birthdayNotifiedAdmin");
        }
      } catch (err) {
        console.error(err);
        alert("Failed to load data for Project-wise Summary");
      }
    })();
  }, []);

  useEffect(() => {
    if (!logs.length) return;

    const validDates = logs
      .map((l) => new Date(l.date))
      .filter((d) => !Number.isNaN(d.getTime()))
      .sort((a, b) => b - a);

    if (!validDates.length) return;

    const now = new Date();
    const currentYear = String(now.getFullYear());
    const currentMonth = String(now.getMonth() + 1).padStart(2, "0");

    const hasCurrentMonth = validDates.some(
      (d) =>
        String(d.getFullYear()) === currentYear &&
        String(d.getMonth() + 1).padStart(2, "0") === currentMonth,
    );

    if (hasCurrentMonth) {
      setSelectedYear(currentYear);
      setSelectedMonth(currentMonth);
    } else {
      const latest = validDates[0];
      setSelectedYear(String(latest.getFullYear()));
      setSelectedMonth(String(latest.getMonth() + 1).padStart(2, "0"));
    }
  }, [logs.length]);

  const yearOptions = useMemo(() => {
    return Array.from(
      new Set(
        logs
          .map((l) => new Date(l.date))
          .filter((d) => !Number.isNaN(d.getTime()))
          .map((d) => String(d.getFullYear())),
      ),
    ).sort((a, b) => Number(b) - Number(a));
  }, [logs]);

  const availableMonths = useMemo(() => {
    if (!selectedYear) return new Set(MONTH_OPTIONS.map((m) => m.value));

    return new Set(
      logs
        .map((l) => new Date(l.date))
        .filter(
          (d) =>
            !Number.isNaN(d.getTime()) &&
            String(d.getFullYear()) === String(selectedYear),
        )
        .map((d) => String(d.getMonth() + 1).padStart(2, "0")),
    );
  }, [logs, selectedYear]);

  const periodLabel = useMemo(
    () => getPeriodLabel(selectedYear, selectedMonth),
    [selectedYear, selectedMonth],
  );

  const filteredLogs = useMemo(() => {
    if (!logs.length) return [];
    if (useRange && fromDate && toDate) {
      const from = new Date(fromDate);
      const to = new Date(toDate);
      return logs.filter((l) => {
        const d = new Date(l.date);
        return d >= from && d <= to;
      });
    }
    return logs.filter((l) => {
      const d = new Date(l.date);
      if (Number.isNaN(d.getTime())) return false;

      if (selectedYear && String(d.getFullYear()) !== String(selectedYear)) {
        return false;
      }

      if (
        selectedMonth &&
        String(d.getMonth() + 1).padStart(2, "0") !== selectedMonth
      ) {
        return false;
      }

      return true;
    });
  }, [logs, useRange, fromDate, toDate, selectedYear, selectedMonth]);

  const projectSummaryByCategory = useMemo(() => {
    const categoryMap = {};
    const allNames = new Set();

    filteredLogs.forEach((log) => {
      const project = (log.projectName || "Unnamed").trim();
      const emp = log.user?.name || "Unknown";
      const hours = parseFloat(log.actualHours) || 0;
      const cat = normalizeCategory(log);

      categoryMap[cat] ||= {};
      categoryMap[cat][project] ||= {
        actual: 0,
        employeeHours: {},
        projectCost: 0,
      };
      const rate = Number(log.hourlyRateAtEntry || 0);
      const cost = Number((hours * rate).toFixed(2));

      categoryMap[cat][project].actual += hours;
      categoryMap[cat][project].projectCost += cost;

      categoryMap[cat][project].employeeHours[emp] =
        (categoryMap[cat][project].employeeHours[emp] || 0) + hours;

      allNames.add(emp);
    });

    for (const cat in categoryMap) {
      for (const proj in categoryMap[cat]) {
        categoryMap[cat][proj].actual = Number(
          categoryMap[cat][proj].actual || 0,
        ).toFixed(2);

        categoryMap[cat][proj].actual = Number(categoryMap[cat][proj].actual);

        categoryMap[cat][proj].projectCost = Number(
          categoryMap[cat][proj].projectCost || 0,
        ).toFixed(2);

        categoryMap[cat][proj].projectCost = Number(
          categoryMap[cat][proj].projectCost,
        );
      }
    }

    setEmployeeNames(Array.from(allNames).sort());
    return categoryMap;
  }, [filteredLogs, empRates]);

  const grandTotals = useMemo(() => {
    let hours = 0;
    let cost = 0;
    const empTotals = Object.fromEntries(employeeNames.map((e) => [e, 0]));

    Object.values(projectSummaryByCategory).forEach((projects) => {
      Object.values(projects).forEach((data) => {
        hours += data.actual || 0;
        cost += data.projectCost || 0;
        employeeNames.forEach((e) => {
          empTotals[e] += data.employeeHours[e] || 0;
        });
      });
    });

    return { hours: +hours.toFixed(2), cost: +cost.toFixed(2), empTotals };
  }, [projectSummaryByCategory, employeeNames]);

  const activeProjectStats = useMemo(() => {
    const counts = {};
    let total = 0;
    Object.entries(projectSummaryByCategory).forEach(([cat, projs]) => {
      const c = Object.keys(projs).length;
      counts[cat] = c;
      total += c;
    });
    return {
      counts: Object.fromEntries(
        Object.entries(counts).sort((a, b) => a[0].localeCompare(b[0])),
      ),
      total,
    };
  }, [projectSummaryByCategory]);

  const activeEmployeeStats = useMemo(() => {
    const catEmpHours = {};
    const all = new Set();

    filteredLogs.forEach((log) => {
      const emp = log.user?.name || "Unknown";
      const hours = parseFloat(log.actualHours) || 0;
      if (hours <= 0) return;
      const cat = normalizeCategory(log);
      catEmpHours[cat] ||= {};
      catEmpHours[cat][emp] = (catEmpHours[cat][emp] || 0) + hours;
      all.add(emp);
    });

    const counts = {};
    Object.entries(catEmpHours).forEach(([cat, map]) => {
      counts[cat] = Object.keys(map).length;
    });

    const top = {};
    Object.entries(catEmpHours).forEach(([cat, map]) => {
      let bestEmp = null;
      let bestHours = -Infinity;
      Object.entries(map).forEach(([emp, h]) => {
        if (h > bestHours) {
          bestHours = h;
          bestEmp = emp;
        }
      });
      if (bestEmp) {
        top[cat] = { name: bestEmp, hours: +Number(bestHours).toFixed(2) };
      }
    });

    const sortedCounts = Object.fromEntries(
      Object.entries(counts).sort((a, b) => a[0].localeCompare(b[0])),
    );

    return { counts: sortedCounts, total: all.size, top };
  }, [filteredLogs]);

  const fetchPendingLeaves = async () => {
    try {
      const res = await api.get("/leaves", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const data = Array.isArray(res.data) ? res.data : [];
      const pending = data.filter((l) => {
        if ((l.status || "").toLowerCase() !== "pending" || !l.fromDate) {
          return false;
        }

        const d = new Date(l.fromDate);
        if (Number.isNaN(d.getTime())) return false;

        if (useRange && fromDate && toDate) {
          const from = new Date(fromDate);
          const to = new Date(toDate);
          return d >= from && d <= to;
        }

        if (selectedYear && String(d.getFullYear()) !== String(selectedYear)) {
          return false;
        }

        if (
          selectedMonth &&
          String(d.getMonth() + 1).padStart(2, "0") !== selectedMonth
        ) {
          return false;
        }

        return true;
      });
      setPendingLeavesCount(pending.length);

      const ids = pending.map((p) => String(p._id));
      const prev = new Set(lastPendingIds);
      const isNew = ids.find((id) => !prev.has(id));
      if (isNew && ids.length >= lastPendingIds.length) {
        notify("New Leave Request", "An employee has requested a leave.");
      }
      setLastPendingIds(ids);
    } catch {
      console.warn("Failed to fetch pending leaves");
    }
  };
  useEffect(() => {
    const fetchOwnBirthday = async () => {
      try {
        const res = await api.get("/birthday-message/me", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        setShowAdminOwnBirthday(res.data.show);
        setAdminOwnBirthday(res.data.message);
      } catch (err) {
        console.error(err);
      }
    };

    fetchOwnBirthday();
  }, []);
  useEffect(() => {
    fetchPendingLeaves();
  }, [selectedYear, selectedMonth, useRange, fromDate, toDate]);

  useEffect(() => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    pollTimerRef.current = setInterval(fetchPendingLeaves, 30_000);
    return () => pollTimerRef.current && clearInterval(pollTimerRef.current);
  }, []);

  useEffect(() => {
    const socket = window?.socket || window?.io?.() || null;
    if (!socket || !socket.on) return;
    const onCreated = () => fetchPendingLeaves();
    const onStatusChanged = () => fetchPendingLeaves();
    try {
      socket.on("leave:created", onCreated);
      socket.on("leave:statusChanged", onStatusChanged);
    } catch {}
    return () => {
      try {
        socket.off?.("leave:created", onCreated);
        socket.off?.("leave:statusChanged", onStatusChanged);
      } catch {}
    };
  }, []);

  const categoryProjectPieData = useMemo(() => {
    const out = {};
    Object.entries(projectSummaryByCategory).forEach(([cat, projs]) => {
      const rows = Object.entries(projs)
        .map(([proj, data], idx) => ({
          key: proj,
          label: proj,
          value: Math.max(Number(data.actual) || 0, 0),
          color: COLORS[idx % COLORS.length],
        }))
        .filter((s) => s.value > 0);

      const total = rows.reduce((s, r) => s + r.value, 0) || 1;
      const sorted = rows.sort((a, b) => b.value - a.value);
      const majors = [];
      const minors = [];
      sorted.forEach((r, i) => {
        const share = r.value / total;
        if (share < 0.03 || i >= 12) minors.push(r);
        else majors.push(r);
      });
      if (minors.length) {
        majors.push({
          key: "__others__",
          label: "Others",
          value: minors.reduce((s, r) => s + r.value, 0),
          color: "#9CA3AF",
        });
      }
      out[cat] = majors;
    });
    return out;
  }, [projectSummaryByCategory]);

  const projectSummaryForTable = useMemo(() => {
    const out = {};
    const min = costMin === "" ? -Infinity : Number(costMin);
    const max = costMax === "" ? Infinity : Number(costMax);
    Object.entries(projectSummaryByCategory).forEach(([cat, projects]) => {
      let entries = Object.entries(projects).filter(
        ([, d]) => d.projectCost >= min && d.projectCost <= max,
      );
      if (costSort === "asc") {
        entries.sort(
          (a, b) => (a[1].projectCost || 0) - (b[1].projectCost || 0),
        );
      } else if (costSort === "desc") {
        entries.sort(
          (a, b) => (b[1].projectCost || 0) - (a[1].projectCost || 0),
        );
      }
      out[cat] = Object.fromEntries(entries);
    });
    return out;
  }, [projectSummaryByCategory, costMin, costMax, costSort]);

  const handleDownloadPDF = async () => {
    try {
      const mod = await import("jspdf");
      const jsPDF = mod.jsPDF || mod.default;
      if (!jsPDF) throw new Error("jsPDF not available");

      const FONT = { title: 24, header: 16, row: 14, cat: 16, foot: 16 };
      const ROW = { headerH: 14, minH: 13, lineH: 8, padY: 4 };

      const fmtINR = (n) =>
        "INR " +
        new Intl.NumberFormat("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(n || 0));

      const fmtHr = (n) =>
        new Intl.NumberFormat("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(n || 0));

      const meas = new jsPDF("l", "mm", "a4");
      meas.setFont("helvetica", "bold");
      meas.setFontSize(FONT.header);

      const marginX = 12;
      const topMargin = 22;
      const bottomMargin = 20;

      const projectColBase = 100;
      const totalColBase = 30;
      const costColBase = 46;

      const empColBase = employeeNames.map((e) =>
        Math.max(meas.getTextWidth(e) + 12, 26),
      );

      const rawHeaderWidth =
        projectColBase +
        totalColBase +
        costColBase +
        empColBase.reduce((a, b) => a + b, 0);

      const a0Width = 1189;
      const a0Height = 841;
      const pageWidth = Math.max(
        a0Width,
        Math.ceil(rawHeaderWidth + marginX * 2),
      );
      const pageHeight = a0Height;

      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: [pageWidth, pageHeight],
      });

      const innerWidth = pageWidth - marginX * 2;
      const scale = innerWidth / rawHeaderWidth;

      const projectCol = projectColBase * scale;
      const totalCol = totalColBase * scale;
      const costCol = costColBase * scale;
      const empCols = empColBase.map((w) => w * scale);

      let y = topMargin;

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(FONT.title);
      const title = useRange
        ? `Project Summary (${fromDate || "—"} to ${toDate || "—"})`
        : `Project Summary for ${periodLabel}`;
      pdf.text(title, marginX, y);
      y += 18;

      const renderHeader = () => {
        let xh = marginX;
        pdf.setDrawColor(220, 220, 220);
        pdf.setFillColor(52, 58, 118);
        pdf.rect(xh, y, innerWidth, ROW.headerH, "FD");

        pdf.setTextColor(255, 255, 255);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(FONT.header);

        pdf.text("Project", xh + 3, y + ROW.headerH - 4);
        xh += projectCol;

        pdf.setFont("courier", "bold");
        pdf.text("Total (hrs)", xh + 3, y + ROW.headerH - 4);
        xh += totalCol;

        pdf.text("Cost (INR)", xh + 3, y + ROW.headerH - 4);
        xh += costCol;

        employeeNames.forEach((emp, i) => {
          pdf.setFont("helvetica", "bold");
          pdf.text(String(emp), xh + 3, y + ROW.headerH - 4);
          xh += empCols[i];
        });

        y += ROW.headerH;
        pdf.setTextColor(0, 0, 0);
      };

      renderHeader();

      pdf.setLineHeightFactor(1.1);
      const pageBottom = pageHeight - bottomMargin;

      const totals = {
        actual: 0,
        cost: 0,
        emp: Object.fromEntries(employeeNames.map((e) => [e, 0])),
      };

      const drawRowBg = (yy, hh, i) => {
        if (i % 2 === 1) {
          pdf.setFillColor(248, 250, 252);
          pdf.rect(marginX, yy, innerWidth, hh, "F");
        }
      };

      let zebraIndex = 0;

      for (const [category, projects] of Object.entries(
        projectSummaryForTable,
      )) {
        if (!projects || !Object.keys(projects).length) continue;

        if (y + ROW.minH > pageBottom) {
          pdf.addPage([pageWidth, pageHeight], "landscape");
          y = topMargin;
          renderHeader();
        }

        pdf.setFillColor(224, 224, 224);
        pdf.rect(marginX, y, innerWidth, ROW.minH, "F");
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(FONT.cat);
        pdf.text(String(category).toUpperCase(), marginX + 3, y + ROW.minH - 4);
        y += ROW.minH;

        for (const [project, data] of Object.entries(projects)) {
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(FONT.row);

          const projText =
            pdf.getTextWidth(project) > projectCol - 5
              ? pdf.splitTextToSize(project, projectCol - 5)
              : project;
          const lines = Array.isArray(projText) ? projText.length : 1;
          const rowH = Math.max(ROW.minH, lines * ROW.lineH + ROW.padY * 2);

          if (y + rowH > pageBottom) {
            pdf.addPage([pageWidth, pageHeight], "landscape");
            y = topMargin;
            renderHeader();
          }

          drawRowBg(y, rowH, zebraIndex++);
          let cx = marginX;

          pdf.setFont("helvetica", "normal");
          pdf.rect(cx, y, projectCol, rowH);
          pdf.text(projText, cx + 3, y + ROW.padY + 6);
          cx += projectCol;

          pdf.setFont("courier", "bold");

          pdf.rect(cx, y, totalCol, rowH);
          pdf.text(fmtHr(data.actual || 0), cx + 3, y + ROW.padY + 6);
          totals.actual += data.actual || 0;
          cx += totalCol;

          pdf.rect(cx, y, costCol, rowH);
          pdf.text(fmtINR(data.projectCost || 0), cx + 3, y + ROW.padY + 6);
          totals.cost += Number(data.projectCost) || 0;
          cx += costCol;

          employeeNames.forEach((emp, i) => {
            const colW = empCols[i];
            pdf.rect(cx, y, colW, rowH);
            const h = data.employeeHours[emp]
              ? fmtHr(data.employeeHours[emp])
              : "";
            if (h) pdf.text(h, cx + 3, y + ROW.padY + 6);
            totals.emp[emp] += data.employeeHours[emp] || 0;
            cx += colW;
          });

          y += rowH;
        }
      }

      if (y + ROW.minH > pageBottom) {
        pdf.addPage([pageWidth, pageHeight], "landscape");
        y = topMargin;
        renderHeader();
      }

      let gx = marginX;
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(FONT.foot);
      pdf.setFillColor(240, 240, 240);
      pdf.rect(gx, y, innerWidth, ROW.minH, "F");

      pdf.rect(gx, y, projectCol, ROW.minH);
      pdf.text("Grand Total", gx + 3, y + ROW.minH - 4);
      gx += projectCol;

      pdf.setFont("courier", "bold");
      pdf.rect(gx, y, totalCol, ROW.minH);
      pdf.text(fmtHr(totals.actual), gx + 3, y + ROW.minH - 4);
      gx += totalCol;

      pdf.rect(gx, y, costCol, ROW.minH);
      pdf.text(fmtINR(totals.cost), gx + 3, y + ROW.minH - 4);
      gx += costCol;

      employeeNames.forEach((emp, i) => {
        const colW = empCols[i];
        const et = totals.emp[emp] ? fmtHr(totals.emp[emp]) : "";
        pdf.rect(gx, y, colW, ROW.minH);
        if (et) pdf.text(et, gx + 3, y + ROW.minH - 4);
        gx += colW;
      });

      pdf.save("Project_Summary.pdf");
    } catch (err) {
      console.error("PDF export failed:", err);
      alert("PDF export failed. Please ensure 'jspdf' is installed.");
    }
  };

  const handleSecureToggleCost = () => {
    if (showCost) {
      setShowCost(false);
      return;
    }
    setPinPopup(true);
  };

  const verifyPIN = () => {
    if (pinInput === SECURE_PIN) {
      setShowCost(true);
      setPinPopup(false);
      setPinInput("");
    } else {
      alert("Incorrect PIN");
      setPinInput("");
    }
  };

  return (
    <div
      style={{
        ...styles.container,
        padding: isMobile ? "14px" : "24px",
      }}
    >
      {showAdminOwnBirthday && <BirthdayConfettiOverlay userName="Admin" />}
      {/* Header */}
      <div
        style={{
          ...styles.headerBar,
          gridTemplateColumns: isMobile ? "1fr 40px" : "1fr auto 1fr",
        }}
      >
        {!isMobile && <div />}
        <div style={styles.headerCenter}>
          <h1 style={styles.bigHeading}>ADMIN DASHBOARD</h1>
          {showAdminOwnBirthday && (
            <div
              style={{
                background: "linear-gradient(135deg, #fff7ed, #ffe4e6)",
                border: "1px solid #fda4af",
                borderRadius: 14,
                padding: "16px 18px",
                marginBottom: 16,
                boxShadow: "0 6px 16px rgba(0,0,0,0.08)",
                whiteSpace: "pre-line",
                lineHeight: 1.7,
                fontSize: 14,
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  color: "#9d174d",
                  marginBottom: 6,
                  fontSize: 16,
                }}
              >
                🎉 Happy Birthday 🎂
              </div>

              {adminOwnBirthday}
            </div>
          )}
          <div
            style={{
              ...styles.subTitle,
              fontSize: isMobile ? 12.5 : 13.5,
            }}
          >
            {(() => {
              const hr = new Date().getHours();
              const greet =
                hr < 12
                  ? "Good morning"
                  : hr < 17
                    ? "Good afternoon"
                    : "Good evening";
              const scope = useRange
                ? "date range overview"
                : `${periodLabel} overview`;
              return `${greet}, Admin — here’s your ${scope}.`;
            })()}{" "}
            👋
          </div>
        </div>
        <div style={styles.bellWrap} title="Pending leave requests">
          <span style={styles.bellIcon}>🔔</span>
          {pendingLeavesCount > 0 && (
            <span style={styles.badge}>
              {pendingLeavesCount > 99 ? "99+" : pendingLeavesCount}
            </span>
          )}
        </div>
      </div>

      {/* 🎂 Birthday Section */}
      {/* Birthday + Leave Row */}
      {(birthdayUsers.length > 0 || pendingLeavesCount > 0) && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: 12,
            marginTop: 10,
            marginBottom: 12,
            alignItems: "stretch",
          }}
        >
          {/* 🎂 Birthday Section */}
          <div
            style={{
              background: "#fff0f6",
              border: "1px solid #f9a8d4",
              padding: "14px 16px",
              borderRadius: 12,
              boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
              minHeight: 92,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            {birthdayUsers.length > 0 ? (
              <>
                <div style={{ fontWeight: 800, fontSize: 14 }}>
                  🎂 Birthday Reminder
                </div>

                <div style={{ marginTop: 6, lineHeight: 1.6 }}>
                  {birthdayUsers.map((u) => (
                    <div key={u._id}>
                      Hello Admin, this is a reminder that today is{" "}
                      <b>{u.name}</b>'s birthday. Please wish {u.name}.
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div
                  style={{ fontWeight: 800, fontSize: 14, color: "#6b7280" }}
                >
                  🎂 Birthday Reminder
                </div>
                <div style={{ marginTop: 6, color: "#6b7280" }}>
                  No birthdays today.
                </div>
              </>
            )}
          </div>

          {/* 📝 Leave Section */}
          <div
            style={{
              background:
                "linear-gradient(180deg, rgba(255,245,230,1) 0%, rgba(255,250,240,1) 100%)",
              border: "1px solid #fde68a",
              borderRadius: 12,
              padding: "14px 16px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
              minHeight: 92,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            {pendingLeavesCount > 0 ? (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 22 }}>📝</span>
                  <div>
                    <div style={{ fontWeight: 800 }}>
                      {pendingLeavesCount} leave request
                      {pendingLeavesCount > 1 ? "s" : ""} awaiting approval
                    </div>
                    <div style={{ fontSize: 13, color: "#4b5563" }}>
                      Go to Leave Requests to review and take action.
                    </div>
                  </div>
                </div>

                <Link to="/leave-application" style={styles.noticeBtn}>
                  Review now
                </Link>
              </>
            ) : (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 22 }}>📝</span>
                  <div>
                    <div style={{ fontWeight: 800, color: "#6b7280" }}>
                      No pending leave requests
                    </div>
                    <div style={{ fontSize: 13, color: "#6b7280" }}>
                      All leave requests are up to date.
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={styles.topFiltersBar}>
        <select
          disabled={useRange}
          value={selectedYear}
          onChange={(e) => {
            setSelectedYear(e.target.value);
            setSelectedMonth("");
          }}
          style={{
            ...styles.input,
            width: 120,
            minWidth: 120,
            maxWidth: 120,
          }}
          title="Select Year"
        >
          {yearOptions.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>

        <select
          disabled={useRange}
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          style={{
            ...styles.input,
            width: 140,
            minWidth: 140,
            maxWidth: 140,
          }}
          title="Select Month"
        >
          <option value="">All Months</option>
          {MONTH_OPTIONS.map((m) => (
            <option
              key={m.value}
              value={m.value}
              disabled={!availableMonths.has(m.value)}
            >
              {m.label}
            </option>
          ))}
        </select>

        <button
          onClick={() => setUseRange((v) => !v)}
          style={{ ...styles.button, backgroundColor: "#673AB7" }}
          type="button"
        >
          📅 {useRange ? "Use Year / Month" : "Use Date Range"}
        </button>

        <button
          onClick={handleDownloadPDF}
          type="button"
          style={styles.buttonOutline}
          title="Download Project-wise Summary as PDF"
        >
          ⬇️ Download Summary PDF
        </button>

        {useRange && (
          <div style={{ ...styles.rangeGroup, width: "100%" }}>
            <label>From:&nbsp;</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              style={{ ...styles.input, width: isMobile ? "100%" : undefined }}
            />
            <label style={{ marginLeft: 8 }}>To:&nbsp;</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              style={{ ...styles.input, width: isMobile ? "100%" : undefined }}
            />
          </div>
        )}
      </div>

      {/* KPI: Active Projects */}
      <div style={styles.kpiSectionProjects}>
        <h3 style={styles.kpiHeader}>
          📌 Total Number of Active Projects —{" "}
          {useRange ? "Selected Date Range" : periodLabel}
        </h3>
        <div style={styles.cardsGrid}>
          {Object.entries(activeProjectStats.counts).map(([cat, count]) => (
            <div key={cat} style={styles.kpiCard}>
              <div style={styles.kpiTop}>
                <span style={styles.kpiBadgeProjects}>{cat.toUpperCase()}</span>
              </div>
              <div style={styles.kpiValue}>{count}</div>
              <div style={styles.kpiCaption}>Active Projects</div>
            </div>
          ))}
          <div style={{ ...styles.kpiCard, ...styles.kpiCardPrimaryProjects }}>
            <div style={styles.kpiValueLarge}>{activeProjectStats.total}</div>
            <div
              style={{ ...styles.kpiCaption, color: "rgba(255,255,255,0.95)" }}
            >
              Total Active Projects
            </div>
          </div>
        </div>
      </div>

      {/* Category-wise pies */}
      <div style={styles.card}>
        <h3 style={{ ...styles.subHeading, textAlign: "center" }}>
          📊 Category-wise Project Pie Charts (by Hours)
        </h3>

        <div
          style={{
            ...styles.multiPieGrid,
            gridTemplateColumns: isMobile
              ? "1fr"
              : isTablet
                ? "repeat(2,1fr)"
                : "repeat(3,1fr)",
            gap: isMobile ? 12 : 20,
          }}
        >
          {Object.entries(categoryProjectPieData).map(([cat, slices]) => (
            <div
              key={cat}
              style={{ ...styles.miniPieCard, minHeight: isMobile ? 230 : 360 }}
            >
              <div style={styles.miniPieHeader}>{cat.toUpperCase()}</div>
              <div style={styles.pieCentered}>
                <DonutPie
                  data={slices}
                  size={PIE_SIZE}
                  thickness={PIE_THICKNESS}
                  onHover={showTooltip}
                  onSliceClick={(info) => {
                    if (!info || info.key === "__others__") return;

                    navigate(
                      `/project-cost-report?project=${encodeURIComponent(
                        info.label,
                      )}&view=combined&range=tillDate`,
                    );
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* KPI: Active Employees */}
      <div style={styles.kpiSectionEmployees}>
        <h3 style={styles.kpiHeader}>
          👥 Total Number of Active Employees —{" "}
          {useRange ? "Selected Date Range" : periodLabel}
        </h3>
        <div style={styles.cardsGrid}>
          {Object.entries(activeEmployeeStats.counts).map(([cat, count]) => {
            const t = activeEmployeeStats.top?.[cat];
            return (
              <div key={cat} style={styles.kpiCard}>
                <div style={styles.kpiTop}>
                  <span style={styles.kpiBadgeEmployees}>
                    {cat.toUpperCase()}
                  </span>
                </div>
                <div style={styles.kpiValue}>{count}</div>
                <div style={styles.kpiCaption}>Active Employees</div>
                {t ? (
                  <div style={styles.kpiSubnote}>
                    Top: <b>{t.name}</b> · {t.hours.toFixed(2)} h
                  </div>
                ) : (
                  <div style={styles.kpiSubnoteMuted}>Top: —</div>
                )}
              </div>
            );
          })}
          <div style={{ ...styles.kpiCard, ...styles.kpiCardPrimaryEmployees }}>
            <div style={styles.kpiValueLarge}>{activeEmployeeStats.total}</div>
            <div
              style={{ ...styles.kpiCaption, color: "rgba(255,255,255,0.95)" }}
            >
              Total Active Employees
            </div>
          </div>
        </div>
      </div>

      <h2 style={styles.heading}>Project-wise Summary</h2>

      {isMobile ? (
        <MobileProjectSummary summary={projectSummaryByCategory} />
      ) : (
        <>
          <div style={styles.costBar}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
              }}
            >
              <strong>Cost filter:</strong>
              <label>Sort</label>
              <select
                value={costSort}
                onChange={(e) => setCostSort(e.target.value)}
                style={styles.inputSm}
              >
                <option value="none">None</option>
                <option value="desc">High → Low</option>
                <option value="asc">Low → High</option>
              </select>

              <label>Min ₹</label>
              <input
                type="number"
                value={costMin}
                onChange={(e) => setCostMin(e.target.value)}
                style={styles.inputSm}
              />

              <label>Max ₹</label>
              <input
                type="number"
                value={costMax}
                onChange={(e) => setCostMax(e.target.value)}
                style={styles.inputSm}
              />

              <button
                type="button"
                onClick={() => {
                  setCostSort("none");
                  setCostMin("");
                  setCostMax("");
                }}
                style={styles.buttonLight}
              >
                Reset
              </button>
            </div>

            <button
              type="button"
              onClick={handleSecureToggleCost}
              style={{
                ...styles.buttonLight,
                backgroundColor: showCost ? "#dcfce7" : "#fee2e2",
                borderColor: showCost ? "#16a34a" : "#ef4444",
              }}
            >
              {showCost ? "👁️ Hide Cost" : "🔒 Show Cost "}
            </button>

            {pinPopup && (
              <div
                style={{
                  position: "fixed",
                  inset: 0,
                  background: "rgba(0,0,0,0.45)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 2000,
                }}
              >
                <div
                  style={{
                    background: "#fff",
                    padding: 22,
                    borderRadius: 12,
                    width: 260,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                    textAlign: "center",
                  }}
                >
                  <h3
                    style={{ fontSize: 16, fontWeight: 800, marginBottom: 14 }}
                  >
                    Enter PIN to View Cost
                  </h3>

                  <input
                    type="password"
                    value={pinInput}
                    onChange={(e) => setPinInput(e.target.value)}
                    maxLength={4}
                    style={{
                      width: "95%",
                      padding: "10px 12px",
                      fontSize: 18,
                      fontWeight: 700,
                      textAlign: "center",
                      letterSpacing: 4,
                      border: "1px solid #ccc",
                      borderRadius: 8,
                      marginBottom: 16,
                    }}
                  />

                  <button
                    onClick={verifyPIN}
                    style={{
                      ...styles.button,
                      width: "100%",
                      marginBottom: 8,
                    }}
                  >
                    Unlock
                  </button>

                  <button
                    onClick={() => setPinPopup(false)}
                    style={{
                      ...styles.buttonLight,
                      width: "100%",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          <div style={{ ...styles.tableWrapper, maxHeight: 620 }}>
            <table style={{ ...styles.table, tableLayout: "fixed" }}>
              <colgroup>
                <col style={{ width: PROJECT_COL_WIDTH }} />
                <col style={{ width: TOTAL_COL_WIDTH }} />
                <col style={{ width: COST_COL_WIDTH }} />
                {employeeNames.map((_, i) => (
                  <col key={i} style={{ minWidth: EMP_COL_MIN }} />
                ))}
              </colgroup>

              <thead>
                <tr>
                  <th
                    style={{
                      ...styles.th,
                      ...styles.thStickyLeft,
                      background: "#343a76",
                      color: "#fff",
                    }}
                  >
                    Project
                  </th>
                  <th
                    style={{
                      ...styles.th,
                      background: "#343a76",
                      color: "#fff",
                    }}
                  >
                    Total
                  </th>
                  {showCost && (
                    <th
                      style={{
                        ...styles.th,
                        background: "#343a76",
                        color: "#fff",
                      }}
                    >
                      Project Cost
                    </th>
                  )}
                  {employeeNames.map((emp) => (
                    <th
                      key={emp}
                      title={emp}
                      style={{
                        ...styles.th,
                        ...styles.thEmp,
                        background: "#343a76",
                        color: "#fff",
                      }}
                    >
                      {emp}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {Object.entries(projectSummaryForTable).map(
                  ([category, projects]) => {
                    const categoryTotal = {
                      actual: 0,
                      cost: 0,
                      employeeHours: Object.fromEntries(
                        employeeNames.map((e) => [e, 0]),
                      ),
                    };

                    Object.values(projects).forEach((data) => {
                      categoryTotal.actual += data.actual;
                      categoryTotal.cost += data.projectCost;
                      employeeNames.forEach((emp) => {
                        categoryTotal.employeeHours[emp] +=
                          data.employeeHours[emp] || 0;
                      });
                    });

                    const rows = Object.entries(projects).map(
                      ([project, data], i) => (
                        <tr key={i}>
                          <td
                            style={{
                              ...styles.stickyCol,
                              border: "1px solid #ddd",
                              background: "#fff",
                            }}
                          >
                            <Link
                              to={`/messenger/project-communication?project=${encodeURIComponent(
                                project,
                              )}`}
                              state={{ openProjectName: project }}
                              title="Open in Projects Communication"
                              style={{
                                cursor: "pointer",
                                color: "#1f3a8a",
                                fontWeight: 700,
                                textDecoration: "none",
                              }}
                            >
                              {project}
                            </Link>
                          </td>
                          <td style={styles.td}>
                            {formatHoursMinutes(data.actual)}
                          </td>
                          {showCost && (
                            <td style={styles.td}>
                              ₹{formatINR(data.projectCost)}
                            </td>
                          )}
                          {employeeNames.map((emp) => (
                            <td key={emp} style={styles.td}>
                              {data.employeeHours[emp]
                                ? formatHoursMinutes(data.employeeHours[emp])
                                : ""}
                            </td>
                          ))}
                        </tr>
                      ),
                    );

                    if (rows.length === 0) return null;

                    return (
                      <React.Fragment key={category}>
                        <tr
                          style={{
                            backgroundColor: "#e0e0e0",
                            fontWeight: "bold",
                          }}
                        >
                          <td
                            style={{
                              ...styles.stickyGrey,
                              border: "1px solid #ddd",
                              background: "#e0e0e0",
                            }}
                          >
                            {category.toUpperCase()}
                          </td>
                          <td style={styles.td}>
                            {formatHoursMinutes(categoryTotal.actual)}
                          </td>
                          {showCost && (
                            <td style={styles.td}>
                              ₹{formatINR(categoryTotal.cost)}
                            </td>
                          )}
                          {employeeNames.map((emp) => (
                            <td key={emp} style={styles.td}>
                              {formatHoursMinutes(
                                categoryTotal.employeeHours[emp],
                              )}
                            </td>
                          ))}
                        </tr>
                        {rows}
                      </React.Fragment>
                    );
                  },
                )}
              </tbody>

              <tfoot>
                <tr>
                  <td
                    style={{ ...styles.tfootCell, ...styles.tfootStickyLeft }}
                  >
                    Grand Total
                  </td>
                  <td style={styles.tfootCell}>
                    {formatHoursMinutes(grandTotals.hours)}
                  </td>
                  {showCost && (
                    <td style={styles.tfootCell}>
                      ₹{formatINR(grandTotals.cost)}
                    </td>
                  )}
                  {employeeNames.map((emp) => (
                    <td key={emp} style={styles.tfootCell}>
                      {(grandTotals.empTotals[emp] || 0).toFixed(2)}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}

      {tooltip.show && (
        <div
          style={{
            position: "fixed",
            top: tooltip.y,
            left: tooltip.x,
            background: "#111827",
            color: "#fff",
            padding: "8px 10px",
            borderRadius: 8,
            fontSize: 12,
            boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
            pointerEvents: "none",
            zIndex: 1000,
            maxWidth: 360,
            whiteSpace: "nowrap",
          }}
        >
          <div style={{ fontWeight: 800, marginBottom: 2 }}>
            {tooltip.label}
          </div>
          <div>
            {tooltip.value.toFixed(2)} hrs · {tooltip.pct}%
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------------- Styles ---------------- */
const styles = {
  container: { padding: "24px", background: "#f4f6fb" },

  headerBar: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    gap: 12,
    marginBottom: 6,
  },
  headerCenter: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  bigHeading: {
    fontSize: 28,
    fontWeight: 900,
    textAlign: "center",
    margin: "0 0 4px 0",
    letterSpacing: 0.5,
    color: "#222",
  },
  subTitle: {
    fontSize: 13.5,
    color: "#6b7280",
    fontWeight: 600,
    textAlign: "center",
  },

  topFiltersBar: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    margin: "8px 0 12px",
  },
  rangeGroup: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },

  bellWrap: {
    position: "relative",
    width: 40,
    height: 40,
    borderRadius: 8,
    background: "#fff",
    display: "grid",
    placeItems: "center",
    border: "1px solid #e5e7eb",
    justifySelf: "end",
  },
  bellIcon: { fontSize: 20 },
  badge: {
    position: "absolute",
    top: -6,
    right: -6,
    background: "#ef4444",
    color: "#fff",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 800,
    padding: "2px 6px",
    border: "2px solid #fff",
    minWidth: 20,
    textAlign: "center",
  },

  noticeCard: {
    marginTop: 8,
    marginBottom: 6,
    padding: 14,
    background:
      "linear-gradient(180deg, rgba(255,245,230,1) 0%, rgba(255,250,240,1) 100%)",
    border: "1px solid #fde68a",
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  noticeBtn: {
    background: "#343a76",
    color: "#fff",
    textDecoration: "none",
    padding: "8px 12px",
    borderRadius: 8,
    fontWeight: 800,
    whiteSpace: "nowrap",
    border: 0,
    cursor: "pointer",
  },

  heading: {
    fontSize: 22,
    fontWeight: 800,
    textAlign: "center",
    marginBottom: 12,
  },

  costBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 8,
  },
  inputSm: {
    padding: "6px 8px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    background: "#fff",
    width: 120,
  },
  buttonLight: {
    padding: "6px 10px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    background: "#fff",
    cursor: "pointer",
    fontWeight: 700,
  },

  multiPieGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 20,
  },
  miniPieCard: {
    background: "linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    padding: 18,
    boxShadow: "0 6px 16px rgba(0,0,0,0.06)",
    minHeight: 360,
  },
  miniPieHeader: { fontWeight: 800, letterSpacing: 0.4, marginBottom: 10 },
  pieCentered: {
    display: "grid",
    placeItems: "center",
    paddingTop: 6,
    minHeight: 320,
  },

  input: {
    padding: "8px 10px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    background: "#fff",
  },
  button: {
    padding: "8px 14px",
    backgroundColor: "#343a76",
    color: "#fff",
    fontWeight: "bold",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
  },
  buttonOutline: {
    padding: "8px 14px",
    background: "#ffffff",
    color: "#343a76",
    fontWeight: "bold",
    border: "1px solid #c7cbe6",
    borderRadius: 6,
    cursor: "pointer",
  },

  tableWrapper: {
    overflowX: "auto",
    overflowY: "auto",
    maxHeight: 620,
    backgroundColor: "#fff",
    borderRadius: 8,
    marginTop: 8,
    position: "relative",
    boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
  },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 1200 },
  th: {
    color: "#fff",
    padding: 10,
    whiteSpace: "nowrap",
    textAlign: "center",
    position: "sticky",
    top: 0,
    zIndex: 4,
    borderTop: "1px solid #ddd",
    borderBottom: "1px solid #ddd",
  },
  thEmp: {
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
    lineHeight: 1.15,
  },
  thStickyLeft: {
    position: "sticky",
    left: 0,
    zIndex: 6,
    textAlign: "left",
    boxShadow: "2px 0 0 rgba(0,0,0,0.05)",
  },
  td: {
    padding: 10,
    borderBottom: "1px solid #ddd",
    whiteSpace: "nowrap",
    textAlign: "center",
  },
  stickyCol: {
    position: "sticky",
    left: 0,
    background: "#fff",
    zIndex: 5,
    textAlign: "left",
  },
  stickyGrey: {
    position: "sticky",
    left: 0,
    background: "#e0e0e0",
    zIndex: 5,
    textAlign: "left",
    fontWeight: 700,
  },

  tfootCell: {
    position: "sticky",
    bottom: 0,
    background: "#f0f0f0",
    fontWeight: 800,
    padding: 10,
    textAlign: "center",
    borderTop: "1px solid #ccc",
    borderBottom: 0,
    zIndex: 3,
  },
  tfootStickyLeft: { left: 0, textAlign: "left", zIndex: 7 },

  kpiSectionProjects: {
    padding: 20,
    borderRadius: 16,
    background:
      "linear-gradient(180deg, #eef2ff 0%, #eaf0ff 45%, #f6f8ff 100%)",
    border: "1px solid #dee6ff",
    boxShadow: "0 6px 18px rgba(28, 45, 100, 0.08)",
    marginTop: 16,
  },
  kpiSectionEmployees: {
    padding: 20,
    borderRadius: 16,
    background:
      "linear-gradient(180deg, #ecfdf5 0%, #e6fbf6 45%, #f3fef9 100%)",
    border: "1px solid #c7f2e7",
    boxShadow: "0 6px 18px rgba(10, 82, 70, 0.08)",
    marginTop: 16,
  },
  kpiHeader: {
    margin: "0 0 12px 0",
    fontSize: 18,
    fontWeight: 800,
    color: "#1e2a55",
    letterSpacing: 0.3,
    textAlign: "center",
  },
  cardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 14,
  },
  kpiCard: {
    background: "#ffffff",
    border: "1px solid #e8ebf5",
    borderRadius: 14,
    padding: "16px 14px",
    boxShadow:
      "0 8px 20px rgba(17, 24, 39, 0.06), inset 0 -1px 0 rgba(17, 24, 39, 0.02)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    transition: "transform 160ms ease, box-shadow 160ms ease",
  },
  kpiTop: {
    width: "100%",
    display: "flex",
    justifyContent: "flex-start",
    marginBottom: 8,
  },
  kpiValue: {
    fontSize: 30,
    fontWeight: 800,
    color: "#0f172a",
    lineHeight: 1.1,
    margin: "6px 0 2px",
  },
  kpiValueLarge: {
    fontSize: 40,
    fontWeight: 900,
    lineHeight: 1.05,
    margin: "2px 0 6px",
  },
  kpiCaption: {
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 0.4,
    color: "#667085",
  },
  kpiSubnote: {
    marginTop: 6,
    fontSize: 12.5,
    color: "#065f46",
    fontWeight: 700,
  },
  kpiSubnoteMuted: {
    marginTop: 6,
    fontSize: 12.5,
    color: "#6b7280",
    fontWeight: 600,
  },

  kpiCardPrimaryProjects: {
    background:
      "linear-gradient(135deg, #343a76 0%, #2b2f60 55%, #22264d 100%)",
    border: "1px solid rgba(255,255,255,0.15)",
    color: "#ffffff",
  },
  kpiCardPrimaryEmployees: {
    background:
      "linear-gradient(135deg, #16a34a 0%, #0f9d7a 45%, #047857 100%)",
    border: "1px solid rgba(255,255,255,0.18)",
    color: "#ffffff",
  },

  kpiBadgeProjects: {
    display: "inline-block",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    color: "#243b6b",
    background: "linear-gradient(180deg, #f1f5ff 0%, #e6ecff 100%)",
    border: "1px solid #d6defc",
    padding: "6px 10px",
    borderRadius: 999,
  },
  kpiBadgeEmployees: {
    display: "inline-block",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    color: "#065f46",
    background: "linear-gradient(180deg, #e8fff5 0%, #d9fbef 100%)",
    border: "1px solid #b9f5e2",
    padding: "6px 10px",
    borderRadius: 999,
  },

  card: {
    background: "#fff",
    padding: 16,
    borderRadius: 12,
    marginTop: 16,
    border: "1px solid #eaecef",
    boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
  },
  subHeading: { fontSize: 18, marginBottom: 12, fontWeight: 700 },
};

/* -------- Mobile card styles -------- */
const mstyles = {
  catCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 16,
    padding: 14,
    boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
  },
  catTitle: {
    fontWeight: 900,
    letterSpacing: 0.6,
    marginBottom: 6,
  },
  catTotals: {
    display: "flex",
    gap: 16,
    flexWrap: "wrap",
    color: "#6b7280",
    marginBottom: 10,
    fontSize: 13,
  },
  projCard: {
    background: "#f8fafc",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: 12,
  },
  projTitle: {
    fontWeight: 900,
    marginBottom: 6,
    lineHeight: 1.2,
  },
  metaRow: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    flexWrap: "wrap",
  },
  metaItem: { fontWeight: 700, fontSize: 13.5 },
  empHeader: { fontSize: 12, color: "#6b7280", marginTop: 6, marginBottom: 6 },
  pillsWrap: { display: "flex", gap: 8, flexWrap: "wrap" },
  pill: {
    background: "#eef2ff",
    border: "1px solid #dbe2fe",
    color: "#111827",
    padding: "6px 10px",
    borderRadius: 999,
    fontSize: 12.5,
  },
};

export default AdminDashboard;
